import React, { useEffect, useMemo, useState } from "react";
import {
  TagPicker,
  Stack,
  ITag,
  mergeStyles,
  Link,
  Text,
} from "@fluentui/react";
import { styles } from "../ExperimentDetailsForm/experimentDetailsForm.styles";
import { FIELD_TOOLTIPS } from "../ExperimentDetailsForm/ExperimentDetailsValidation/experimentDetailsFormValidation";
import BaseLabelRenderer from "./BaseLabelRenderer";

interface BaseTagFieldProps {
  fieldKey: keyof any;
  label: string;
  selectedItems: ITag[];
  isRequired?: boolean;
  isEdit: boolean;
  subtitle?: string;
  placeholder?: string;
  errorMessage?: string[];
  onChange: (fieldKey: string, items: ITag[]) => void;
  grow?: boolean;
  enableLoadMore?: boolean;
  initialVisibleItems?: number;
  loadMoreItemsCount?: number;
}

const BaseTagField: React.FC<BaseTagFieldProps> = ({
  fieldKey,
  label,
  selectedItems,
  isRequired = false,
  isEdit,
  subtitle,
  placeholder = "Add tag...",
  errorMessage,
  onChange,
  grow = false,
  enableLoadMore = false,
  initialVisibleItems = 200,
  loadMoreItemsCount = 200,
}) => {
  const hasTooltip = FIELD_TOOLTIPS[fieldKey as string];
  const [visibleItemCount, setVisibleItemCount] = useState(initialVisibleItems);

  const showLoadMoreItems =
    enableLoadMore && selectedItems.length > initialVisibleItems;

  useEffect(() => {
    if (!showLoadMoreItems) {
      setVisibleItemCount(initialVisibleItems);
    } else {
      setVisibleItemCount((current) =>
        Math.min(Math.max(current, initialVisibleItems), selectedItems.length),
      );
    }
  }, [initialVisibleItems, selectedItems.length, showLoadMoreItems]);

  const selectedItemsForPicker = useMemo(() => {
    if (!showLoadMoreItems) {
      return selectedItems;
    }
    return selectedItems.slice(0, visibleItemCount);
  }, [selectedItems, showLoadMoreItems, visibleItemCount]);

  const hiddenItemsCount = useMemo(() => {
    return Math.max(selectedItems.length - selectedItemsForPicker.length, 0);
  }, [selectedItems.length, selectedItemsForPicker.length]);

  const tagPickerProps = {
    selectedItems: selectedItemsForPicker,
    onChange: isEdit
      ? (items?: ITag[]) => {
          if (showLoadMoreItems) {
            const hiddenItems = selectedItems.slice(visibleItemCount);
            onChange(fieldKey as string, [...(items || []), ...hiddenItems]);
          } else {
            onChange(fieldKey as string, items || []);
          }
        }
      : undefined,
    onResolveSuggestions: defaultOnResolveSuggestions,
    inputProps: {
      placeholder,
      readOnly: !isEdit,
    },
    removeButtonAriaLabel: `Remove ${label.toLowerCase()}`,
    disabled: !isEdit,
  };

  const content = (
    <>
      <BaseLabelRenderer
        label={label}
        isRequired={isRequired}
        tooltip={hasTooltip}
        description={subtitle}
      />
      <TagPicker
        {...tagPickerProps}
        className={mergeStyles(
          !tagPickerProps.disabled &&
            styles.experimentDetailsTagPickedFieldEnabled,
          tagPickerProps.disabled &&
            styles.experimentDetailsTagPickedFieldDisabled,
          styles.experimentDetailsTagPickerStickyInput,
        )}
      />
      {showLoadMoreItems && hiddenItemsCount > 0 && (
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          styles={{ root: { marginTop: 8 } }}
        >
          <Text
            className={mergeStyles(
              tagPickerProps.disabled &&
                styles.experimentDetailsTagItemsTextDisabled,
            )}
          >
            Showing {selectedItemsForPicker.length.toLocaleString()} of{" "}
            {selectedItems.length.toLocaleString()} items...
          </Text>
          <Link
            title={
              tagPickerProps.disabled
                ? "Load more items disabled (edit details to enable)"
                : "Load more items"
            }
            onClick={() =>
              setVisibleItemCount(
                Math.min(
                  visibleItemCount + loadMoreItemsCount,
                  selectedItems.length,
                ),
              )
            }
            disabled={tagPickerProps.disabled}
          >
            Load more
          </Link>
        </Stack>
      )}
      {errorMessage && errorMessage.length > 0 && (
        <span className={styles.experimentDetailsError}>
          {errorMessage.join("; ")}
        </span>
      )}
    </>
  );

  if (grow) {
    return <Stack grow>{content}</Stack>;
  }

  return content;
};

export const defaultOnResolveSuggestions = (
  filterText: string,
  selectedItems?: ITag[],
): ITag[] => {
  if (!filterText) return [];
  const exists = (selectedItems || []).some(
    (tag) => tag.name.toLowerCase() === filterText.toLowerCase(),
  );
  return exists ? [] : [{ key: filterText, name: filterText }];
};

export default BaseTagField;
